<script setup>
import { ref, computed, watch } from 'vue';
import FluentIcon from 'dashboard/components/FluentIcon/Index.vue';
import { useLineStickerPackages } from 'dashboard/composables/useLineStickerPackages';
import { useAlert } from 'dashboard/composables';
import LineStickerAPI from 'dashboard/api/lineStickers';

const props = defineProps({
  show: {
    type: Boolean,
    default: false,
  },
  conversationId: {
    type: [String, Number],
    required: true,
  },
});

const emit = defineEmits(['close', 'sticker-sent']);

const selectedPackage = ref('');
const selectedSticker = ref('');

// Use composable
const { getEnabledPackages, loadPackagesFromAPI } = useLineStickerPackages();
const enabledPackages = computed(() => getEnabledPackages());

// Auto-select first package and load from API when modal opens
watch(
  () => props.show,
  async newValue => {
    if (newValue) {
      console.log(
        '[LineStickerPicker] Modal opened, loading packages from API...'
      );
      await loadPackagesFromAPI();
      console.log(
        '[LineStickerPicker] Packages loaded, enabled count:',
        enabledPackages.value.length
      );

      if (enabledPackages.value.length > 0 && !selectedPackage.value) {
        selectedPackage.value = enabledPackages.value[0].packageId;
        console.log(
          '[LineStickerPicker] Auto-selected first package:',
          selectedPackage.value
        );
      }
    }
  }
);

const currentPackage = computed(() => {
  return enabledPackages.value.find(
    pkg => pkg.packageId === selectedPackage.value
  );
});

const currentPackageStickers = computed(() => {
  return currentPackage.value?.stickers || [];
});

const canSend = computed(() => {
  return selectedPackage.value && selectedSticker.value;
});

const getStickerUrl = stickerId => {
  return `https://stickershop.line-scdn.net/stickershop/v1/sticker/${stickerId}/android/sticker.png`;
};

const handleImageError = event => {
  event.target.src =
    'data:image/svg+xml,%3Csvg xmlns="http://www.w3.org/2000/svg" width="100" height="100"%3E%3Crect fill="%23e2e8f0" width="100" height="100"/%3E%3Ctext x="50%25" y="50%25" text-anchor="middle" dominant-baseline="middle" fill="%2394a3b8"%3E?%3C/text%3E%3C/svg%3E';
};

const selectPackage = packageId => {
  selectedPackage.value = packageId;
  selectedSticker.value = '';
};

const selectSticker = stickerId => {
  selectedSticker.value = stickerId;
};

const isSending = ref(false);

const sendSticker = async () => {
  console.log('[LineStickerPicker] sendSticker called', {
    canSend: canSend.value,
    isSending: isSending.value,
    packageId: selectedPackage.value,
    stickerId: selectedSticker.value,
    conversationId: props.conversationId,
  });

  if (!canSend.value || isSending.value) {
    console.log('[LineStickerPicker] Cannot send - validation failed');
    return;
  }

  isSending.value = true;

  console.log('[LineStickerPicker] Calling LineStickerAPI.sendSticker...');
  console.log('[LineStickerPicker] Params:', {
    conversationId: props.conversationId,
    packageId: selectedPackage.value,
    stickerId: selectedSticker.value,
  });

  try {
    const response = await LineStickerAPI.sendSticker(
      props.conversationId,
      selectedPackage.value,
      selectedSticker.value
    );

    console.log('[LineStickerPicker] Response received:', response.data);

    // Check if message was created successfully
    // Response could be either {ok: true} or a message object with id
    if (response.data && (response.data.ok || response.data.id)) {
      console.log(
        '[LineStickerPicker] Sticker sent successfully, message ID:',
        response.data.id
      );
      emit('sticker-sent', {
        packageId: selectedPackage.value,
        stickerId: selectedSticker.value,
      });
      useAlert('Sticker sent successfully');
      closeModal();
    } else {
      const errorMsg =
        'Failed to send sticker: ' + (response.data.error || 'Unknown error');
      console.error('[LineStickerPicker]', errorMsg);
      useAlert(errorMsg);
    }
  } catch (error) {
    console.error('[LineStickerPicker] Error caught:', error);
    console.error('[LineStickerPicker] Error response:', error.response);
    console.error(
      '[LineStickerPicker] Error response data:',
      error.response?.data
    );
    console.error(
      '[LineStickerPicker] Error response data.errors:',
      error.response?.data?.errors
    );
    console.error(
      '[LineStickerPicker] Error response status:',
      error.response?.status
    );
    console.error(
      '[LineStickerPicker] Error response headers:',
      error.response?.headers
    );

    // Get detailed error message
    let errorMessage = 'Unknown error';
    if (
      error.response?.data?.errors &&
      Array.isArray(error.response.data.errors)
    ) {
      errorMessage = error.response.data.errors.join(', ');
    } else if (error.response?.data?.error) {
      errorMessage = error.response.data.error;
    } else if (error.response?.data?.message) {
      errorMessage = error.response.data.message;
    } else {
      errorMessage = error.message;
    }

    console.error('[LineStickerPicker] Final error message:', errorMessage);
    useAlert('Failed to send sticker: ' + errorMessage);
  } finally {
    isSending.value = false;
    console.log('[LineStickerPicker] sendSticker completed');
  }
};

const closeModal = () => {
  selectedSticker.value = '';
  emit('close');
};
</script>

<template>
  <div
    v-if="show"
    class="fixed inset-0 z-[9999] flex items-center justify-center bg-black/60 backdrop-blur-sm"
    @click="closeModal"
  >
    <div
      class="bg-white rounded-xl shadow-2xl w-[90%] max-w-2xl max-h-[85vh] flex flex-col animate-slide-up"
      @click.stop
    >
      <!-- Header -->
      <div
        class="flex items-center justify-between p-4 border-b border-slate-200"
      >
        <h3 class="text-lg font-semibold text-slate-900">
          Select LINE Sticker
        </h3>
        <button
          class="p-2 rounded-lg hover:bg-slate-100 transition-colors"
          @click="closeModal"
        >
          <FluentIcon icon="dismiss" size="20" />
        </button>
      </div>

      <!-- Body -->
      <div class="flex-1 overflow-y-auto p-4">
        <!-- Package Tabs -->
        <div class="flex gap-2 mb-4 flex-wrap">
          <button
            v-for="pkg in enabledPackages"
            :key="pkg.packageId"
            class="px-4 py-2 rounded-lg font-medium text-sm transition-all"
            :class="[
              selectedPackage === pkg.packageId
                ? 'bg-woot-500 text-white shadow-md'
                : 'bg-slate-100 text-slate-700 hover:bg-slate-200',
            ]"
            @click="selectPackage(pkg.packageId)"
          >
            {{ pkg.name }}
          </button>
        </div>

        <!-- Sticker Grid -->
        <div class="grid grid-cols-4 sm:grid-cols-5 md:grid-cols-6 gap-3">
          <div
            v-for="stickerId in currentPackageStickers"
            :key="stickerId"
            class="relative flex flex-col items-center p-3 rounded-xl border-2 cursor-pointer transition-all"
            :class="[
              selectedSticker === stickerId
                ? 'border-woot-500 bg-woot-50 shadow-lg scale-105'
                : 'border-slate-200 bg-white hover:border-woot-300 hover:bg-woot-50 hover:scale-105',
            ]"
            @click="selectSticker(stickerId)"
          >
            <img
              :src="getStickerUrl(stickerId)"
              :alt="`Sticker ${stickerId}`"
              class="w-16 h-16 object-contain"
              @error="handleImageError"
            />
            <div
              v-if="selectedSticker === stickerId"
              class="absolute -top-1 -right-1 w-6 h-6 bg-woot-500 rounded-full flex items-center justify-center shadow-md"
            >
              <FluentIcon icon="checkmark" size="14" class="text-white" />
            </div>
          </div>
        </div>
      </div>

      <!-- Footer -->
      <div
        class="flex items-center justify-end gap-3 p-4 border-t border-slate-200"
      >
        <button
          class="px-4 py-2 text-sm font-medium text-slate-700 bg-slate-100 rounded-lg hover:bg-slate-200 transition-colors"
          @click="closeModal"
        >
          Cancel
        </button>
        <button
          :disabled="!canSend || isSending"
          class="px-4 py-2 text-sm font-medium text-white rounded-lg transition-all flex items-center gap-2"
          :class="[
            canSend && !isSending
              ? 'bg-woot-500 hover:bg-woot-600 shadow-md hover:shadow-lg'
              : 'bg-slate-300 cursor-not-allowed',
          ]"
          @click="sendSticker"
        >
          <FluentIcon v-if="!isSending" icon="send" size="16" />
          <FluentIcon v-else icon="spinner" size="16" class="animate-spin" />
          {{ isSending ? 'Sending...' : 'Send Sticker' }}
        </button>
      </div>
    </div>
  </div>
</template>
